// src/components/home/Header.js - Enhanced version
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Image, Button, Container, HStack, Text, useBreakpointValue } from '@chakra-ui/react';
import '../styles/components/Header.css';

// Logo Component for HomePage
const BookDentistLogo = ({ variant = 'dark', size = 'medium', onClick }) => {
  const sizes = {
    small: { fontSize: '1.5rem', transform: 'scale(0.7)' },
    medium: { fontSize: '2rem', transform: 'scale(1)' },
    large: { fontSize: '2.5rem', transform: 'scale(1.2)' }
  };
  
  const currentSize = sizes[size] || sizes.medium;
  
  const lightGradient = "linear-gradient(135deg, #1e3c72 0%, #4a90e2 100%)";
  const darkGradient = "linear-gradient(135deg, #ffffff 0%, #74b9ff 100%)";
  const darkGradient2 = "linear-gradient(135deg, #74b9ff 0%, #ffffff 100%)";
  
  const bookColor = variant === 'light' ? lightGradient : darkGradient;
  const proColor = variant === 'light' ? lightGradient : darkGradient2;
  const dentistColor = variant === 'light' ? '#1e3c72' : 'white';
  
  return (
    <Box
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      display="inline-flex"
      flexDirection="column"
      alignItems="center"
      style={{ transform: currentSize.transform, transformOrigin: 'left center' }}
      transition="all 0.3s ease"
      _hover={onClick ? { transform: `${currentSize.transform} scale(1.05)` } : {}}
    >
      <Box
        fontSize={currentSize.fontSize}
        fontWeight="900"
        letterSpacing="-2px"
        lineHeight="1"
      >
        <Box
          as="span"
          style={{
            background: bookColor,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          Book
        </Box>
        <Box
          as="span"
          color={dentistColor}
        >
          Dentist
        </Box>
        <Box
          as="span"
          style={{
            background: proColor,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          .pro
        </Box>
      </Box>
    </Box>
  );
};

const Header = ({ branding, mode = 'clinic' }) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // HomePage Navigation Header
  if (mode === 'home') {
    return (
      <Box
        className="header-gradient"
        position="relative"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <Container maxW="container.xl">
          <Flex
            justify="space-between"
            align="center"
            py={5}
            px={{ base: 4, md: 0 }}
          >
            <Box>
              <BookDentistLogo 
                variant="dark" 
                size={isMobile ? 'small' : 'medium'}
                onClick={() => navigate('/')}
              />
            </Box>
            
            {!isMobile && (
              <HStack spacing={8} align="center">
                <Text
                  color="white"
                  fontWeight="600"
                  opacity={0.9}
                  cursor="pointer"
                  transition="all 0.3s ease"
                  onClick={() => {
                    const element = document.getElementById('how-it-works');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  _hover={{ opacity: 1, transform: 'translateY(-2px)' }}
                >
                  How It Works
                </Text>
                <Text
                  color="white"
                  fontWeight="600"
                  opacity={0.9}
                  cursor="pointer"
                  transition="all 0.3s ease"
                  onClick={() => {
                    const element = document.getElementById('pricing');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  _hover={{ opacity: 1, transform: 'translateY(-2px)' }}
                >
                  Pricing
                </Text>
                <Text
                  color="white"
                  fontWeight="600"
                  opacity={0.9}
                  cursor="pointer"
                  transition="all 0.3s ease"
                  onClick={() => navigate('/dentistjunobeach')}
                  _hover={{ opacity: 1, transform: 'translateY(-2px)' }}
                >
                  Demo
                </Text>
                <Button
                  onClick={() => navigate('/register')}
                  bg="white"
                  color="#4a90e2"
                  px={6}
                  py={5}
                  borderRadius="10px"
                  fontWeight="700"
                  fontSize="md"
                  boxShadow="0 4px 15px rgba(0, 0, 0, 0.2)"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)"
                  }}
                >
                  Start Free Trial
                </Button>
              </HStack>
            )}
            
            {isMobile && (
              <Button
                onClick={() => navigate('/register')}
                bg="white"
                color="#4a90e2"
                px={5}
                py={4}
                borderRadius="10px"
                fontWeight="700"
                fontSize="sm"
                boxShadow="0 4px 15px rgba(0, 0, 0, 0.2)"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)"
                }}
              >
                Start Free Trial
              </Button>
            )}
          </Flex>
        </Container>
      </Box>
    );
  }

  // Original Clinic Header
  return (
    <Box
        className="clinic-header"
        position="relative"
        overflow="hidden"
    >
        <Box className="header-bg-effect">
            <Box className="bg-orb" />
            <Box className="bg-orb" />
            <Box className="bg-orb" />
            <Box className="sparkle" />
            <Box className="sparkle" />
            <Box className="sparkle" />
            <Box className="sparkle" />
            <Box className="sparkle" />
            <Box className="sparkle" />
        </Box>

        <Box className="header-shine" />

        <Flex
            className="header-content"
            position="relative"
            zIndex={2}
            direction="column"
            align="center"
            justify="center"
            maxW="1200px"
            mx="auto"
            gap={{ base: '1rem', md: '1.5rem' }}
        >
            {branding?.logoUrl && (
                <Box className="logo-container" position="relative">
                    <Box className="logo-glow" />
                    <Box className="logo-outer-ring" />
                    <Flex className="logo-image">
                        <Image
                            src={branding.logoUrl}
                            alt={branding.headerText || 'Clinic Logo'}
                            maxW="100%"
                            maxH="100%"
                            w="auto"
                            h="auto"
                            objectFit="contain"
                            filter="drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))"
                        />
                    </Flex>
                </Box>
            )}

            <Flex className="realtime-badge">
                <Box className="badge-shine" />
                <Box className="live-dot" />
                <Box className="badge-icon">⚡</Box>
                <Box
                    as="span"
                    className="badge-text"
                    fontSize={{ base: '1.05rem', md: '1.2rem', lg: '1.4rem' }}
                >
                    Real-Time 24/7 Scheduling
                </Box>
            </Flex>
        </Flex>
    </Box>
  );
};

export default Header;